import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Store,
  OpenInNew,
} from '@mui/icons-material';
import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_STORE_BY_ID } from '../../graphql/queries';
import StoreAvatar from '../common/StoreAvatar';

interface NavbarProps {
  onMenuClick: () => void;
}

const Navbar = ({ onMenuClick }: NavbarProps) => {
  const { user, logout, storeId: authStoreId } = useAuth();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const storeIdFromPath = location.pathname.match(/\/store\/([^/]+)/)?.[1] ?? null;
  const storeId = storeIdFromPath || authStoreId;

  const { data: storeData } = useQuery(GET_STORE_BY_ID, {
    variables: { storeId },
    skip: !storeId,
  });

  const store = storeData?.storeDetails;

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
        borderBottom: '1px solid',
        borderColor: 'divider',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="toggle drawer"
          onClick={onMenuClick}
          edge="start"
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        
        <Typography
          variant="h6"
          component="div"
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mr: 2,
          }}
        >
          HairX
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {storeId && (
          <Chip
            icon={store?.avatar ? (
              <StoreAvatar
                storeAvatar={store.avatar}
                storeName={store.name}
                sx={{ width: 20, height: 20 }}
              />
            ) : (
              <Store />
            )}
            label={store?.name || `Store: ${storeId}`}
            variant="outlined"
            size="small"
            sx={{ mr: 2, display: { xs: 'none', sm: 'flex' } }}
          />
        )}

        {storeId && (
          <Button
            variant="outlined"
            size="small"
            endIcon={<OpenInNew />}
            href={`${import.meta.env.VITE_CUSTOMER_APP_URL}/barbershop/${storeId}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mr: 2, display: { xs: 'none', sm: 'flex' } }}
          >
            Open Store page
          </Button>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color="inherit"
            onClick={handleMenuClick}
            sx={{ p: 0 }}
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.firstName?.[0] || user?.username?.[0] || 'U'}
            </Avatar>
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleMenuClose}>
              <AccountCircle sx={{ mr: 1 }} />
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
